/**
 * Services API endpoints
 */
import { api } from './baseApi';
import type { Service, SubService, InternalSubService } from '../../types/service';

interface PaginatedResponse<T> {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: T[];
}

interface ServiceResponse {
  success: boolean;
  message?: string;
  data: Service;
}

interface SubServiceResponse {
  success: boolean;
  data: SubService;
}

export const servicesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Services
    getServices: builder.query<PaginatedResponse<Service>, { search?: string; page?: number; page_size?: number }>({
      query: (params) => ({
        url: '/dashboard/services/',
        params,
      }),
      providesTags: ['Services'],
    }),
    getService: builder.query<ServiceResponse, number>({
      query: (id) => `/dashboard/services/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Service', id }],
    }),
    createService: builder.mutation<ServiceResponse, FormData>({
      query: (formData) => ({
        url: '/dashboard/services/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Services'],
    }),
    updateService: builder.mutation<ServiceResponse, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/dashboard/services/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Service', id }, 'Services'],
    }),
    deleteService: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/services/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Services'],
    }),

    // SubServices
    getSubServices: builder.query<PaginatedResponse<SubService>, { service?: number; service_id?: number; search?: string; is_active?: boolean; page?: number; page_size?: number }>({
      query: (params) => {
        // Map service_id to service if provided
        const { service_id, ...rest } = params;
        return {
          url: '/dashboard/subservices/',
          params: service_id ? { ...rest, service: service_id } : rest,
        };
      },
      providesTags: ['SubServices'],
    }),
    getServiceSubServices: builder.query<PaginatedResponse<SubService>, { service_id: number; page?: number; page_size?: number }>({
      query: ({ service_id, ...params }) => ({
        url: `/dashboard/services/${service_id}/subservices/`,
        params,
      }),
      providesTags: (_result, _error, { service_id }) => [{ type: 'SubServices', id: service_id }],
    }),
    getSubService: builder.query<SubServiceResponse, number>({
      query: (id) => `/dashboard/subservices/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'SubService', id }],
    }),
    createSubService: builder.mutation<SubServiceResponse, FormData>({
      query: (formData) => ({
        url: '/dashboard/subservices/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['SubServices'],
    }),
    updateSubService: builder.mutation<SubServiceResponse, { id: number; data: FormData }>({
      query: ({ id, data }) => ({
        url: `/dashboard/subservices/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'SubService', id }, 'SubServices'],
    }),
    deleteSubService: builder.mutation<void, number>({
      query: (id) => ({
        url: `/dashboard/subservices/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubServices'],
    }),

    // Internal SubServices
    getAllInternalSubServices: builder.query<PaginatedResponse<InternalSubService>, { 
      service?: number; 
      parent?: number; 
      is_active?: boolean; 
      search?: string; 
      page?: number; 
      page_size?: number 
    }>({
      query: (params) => {
        const queryParams: Record<string, string> = {};
        if (params.service) queryParams.service = params.service.toString();
        if (params.parent) queryParams.parent = params.parent.toString();
        if (params.is_active !== undefined) queryParams.is_active = params.is_active.toString();
        if (params.search) queryParams.search = params.search;
        if (params.page) queryParams.page = params.page.toString();
        if (params.page_size) queryParams.page_size = params.page_size.toString();
        
        return {
          url: '/dashboard/internal-subservices/',
          params: queryParams,
        };
      },
      providesTags: ['InternalSubService'],
    }),
    getInternalSubServices: builder.query<PaginatedResponse<InternalSubService>, { subservice_id: number; search?: string; page?: number; page_size?: number }>({
      query: ({ subservice_id, ...params }) => ({
        url: `/dashboard/subservices/${subservice_id}/internal-subservices/`,
        params,
      }),
      providesTags: (_result, _error, { subservice_id }) => [{ type: 'InternalSubService', id: subservice_id }],
    }),
    createInternalSubService: builder.mutation<SubServiceResponse, { subservice_id: number; data: FormData }>({
      query: ({ subservice_id, data }) => ({
        url: `/dashboard/subservices/${subservice_id}/internal-subservices/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { subservice_id }) => [
        { type: 'InternalSubService', id: subservice_id },
        { type: 'InternalSubService' }, // Invalidate all internal subservices list
        { type: 'SubService', id: subservice_id },
        'SubServices',
      ],
    }),
    updateInternalSubService: builder.mutation<SubServiceResponse, { subservice_id: number; id: number; data: FormData }>({
      query: ({ subservice_id, id, data }) => ({
        url: `/dashboard/subservices/${subservice_id}/internal-subservices/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { subservice_id }) => [
        { type: 'InternalSubService', id: subservice_id },
        { type: 'InternalSubService' }, // Invalidate all internal subservices list
        { type: 'SubService', id: subservice_id },
        'SubServices',
      ],
    }),
    deleteInternalSubService: builder.mutation<void, { subservice_id: number; id: number }>({
      query: ({ subservice_id, id }) => ({
        url: `/dashboard/subservices/${subservice_id}/internal-subservices/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { subservice_id }) => [
        { type: 'InternalSubService', id: subservice_id },
        { type: 'InternalSubService' }, // Invalidate all internal subservices list
        { type: 'SubService', id: subservice_id },
        'SubServices',
      ],
    }),
  }),
});

export const {
  useGetServicesQuery,
  useGetServiceQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useGetSubServicesQuery,
  useGetServiceSubServicesQuery,
  useGetSubServiceQuery,
  useCreateSubServiceMutation,
  useUpdateSubServiceMutation,
  useDeleteSubServiceMutation,
  useGetAllInternalSubServicesQuery,
  useGetInternalSubServicesQuery,
  useCreateInternalSubServiceMutation,
  useUpdateInternalSubServiceMutation,
  useDeleteInternalSubServiceMutation,
} = servicesApi;

